import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Search, FileSpreadsheet, Loader2 } from "lucide-react";
import { USDollar } from "~/functions";
import api, { baseURL } from "~/services/api";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import useReportsStore from "~/store/reportsSectionStore";
import { getMonth, getYear } from "date-fns";

export default function ReceivablesModal({ isOpen, onClose, modalData }) {
  const { filters } = useReportsStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  const [exportingExcel, setExportingExcel] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm((searchTerm || "").trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const reportToFilter = {
    Received: "received",
    Outstanding: "outstanding",
    Renegotiated: "renegociated",
    "Vacation Free": "vacation_free",
  };

  const periodByLabel = filters?.period_by?.label || filters?.period_by?.value || "Due Date";

  useEffect(() => {
    if (!isOpen) return;
    if (!modalData?.code) return;

    const { from, to } = modalData || {};
    const isCustom = filters?.report === "Custom";

    let params = {};
    if (isCustom) {
      if (!from || !to) return;
      params = {
        period_from: from,
        period_to: to,
        period_by: periodByLabel,
        code: modalData.code,
        payment_method_ids: modalData?.paymentMethodIds || "all",
        statuses: modalData?.statuses || "all",
      };
    } else {
      if (!from || !to) return;
      params = {
        period_from: from,
        period_to: to,
        period_by: periodByLabel,
        filter: reportToFilter[filters?.report] || undefined,
        code: modalData.code,
      };
    }

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setTransactions([]);
        setSummary(null);
        const { data } = await api.get(`/reports/receivables/transactions`, { params });
        setTransactions(Array.isArray(data?.transactions) ? data.transactions : []);
        setSummary({ total: data?.total || 0, code: data?.code, period: data?.period, filter: data?.filter });
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load receivables transactions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, modalData?.code, modalData?.from, modalData?.to, modalData?.paymentMethodIds, modalData?.statuses, periodByLabel, filters?.report, modalData?.period]);

  useEffect(() => {
    setPage(1);
  }, [debouncedTerm, transactions?.length]);

  const filteredRows = useMemo(() => {
    const term = debouncedTerm;
    if (!term) return transactions;
    const norm = (s) => (s || "").toString().toLowerCase();
    return transactions.filter((r) =>
      [r.invoice_number, r.filial_name, r.issuer_name, r.chart_code, r.chart_name, r.memo, r.status]
        .map(norm)
        .some((v) => v.includes(term))
    );
  }, [transactions, debouncedTerm]);

  const totalPages = Math.max(1, Math.ceil((filteredRows?.length || 0) / PAGE_SIZE));
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  const isVacationFree = (summary?.filter === "vacation_free") || (filters?.report === "Vacation Free");

  const handleExportExcel = async () => {
    const { from, to, code } = modalData || {};
    if (!code) {
      toast("Missing params to export", { type: "error", autoClose: 2500 });
      return;
    }
    const isCustom = filters?.report === "Custom";
    let params = {};
    if (isCustom) {
      if (!from || !to) {
        toast("Missing period to export", { type: "error", autoClose: 2500 });
        return;
      }
      params = {
        period_from: from,
        period_to: to,
        period_by: periodByLabel,
        code,
        payment_method_ids: modalData?.paymentMethodIds || "all",
        statuses: modalData?.statuses || "all",
      };
    } else {
      if (!from || !to) {
        toast("Missing period to export", { type: "error", autoClose: 2500 });
        return;
      }
      params = {
        period_from: from,
        period_to: to,
        period_by: periodByLabel,
        filter: reportToFilter[filters?.report] || undefined,
        code,
      };
    }
    setExportingExcel(true);
    try {
      const { data } = await api.get("/reports/receivables/transactions/excel", { params });
      const name = data?.name || `receivables-transactions-${code}.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel gerado", { type: "success", autoClose: 2500 });
    } catch (err) {
      toast(err?.response?.data?.error || "Falha ao gerar Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  };

  if (!isOpen) return null;
  const dateTypeLabelMap = {
    due_date: "Due Date",
    settlement_date: "Settlement Date",
    competence_date: "Competence Date",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(0,0,0,.75)" }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex-1">
            <h2 className="text-xl font-bold">Receivables Details - {modalData?.accountLabel || "Account"}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Period: {modalData?.period || "-"}
              {modalData?.from && modalData?.to ? (
                <>
                  {" "}• From: {modalData.from} to {modalData.to}
                </>
              ) : null}
              {" "}• Value: {modalData?.value != null ? USDollar.format(modalData.value) : "-"}
            </p>
            <div className="mt-2 text-xs text-gray-500">
              <span className="font-semibold">Code:</span> {modalData?.code || "-"} •
              <span className="font-semibold ml-2">Date Type:</span> {periodByLabel}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-700">
            {summary && (
              <span>
                Total: <span className="font-semibold">{USDollar.format(summary.total || 0)}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search invoice, branch, chart, memo, status..."
                className="w-72 pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-orange-500"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            </div>
            <button
              onClick={handleExportExcel}
              disabled={exportingExcel || loading || !summary}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {exportingExcel ? <Loader2 className="animate-spin" size={18} /> : <FileSpreadsheet size={18} />}
              <span>Excel</span>
            </button>
          </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto min-h-0">
          {error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : loading ? (
            <div className="text-sm text-gray-600">Loading...</div>
          ) : (
            <table className="w-full min-w-[1600px] border-collapse">
              <thead className="sticky top-0 bg-gray-100 z-10 shadow-sm">
                <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Invoice</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Branch</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Issuer</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Chart Code</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Chart Name</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Memo</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Amount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Discount</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Fee</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">{isVacationFree ? "Discount (Vacation Free)" : "Total"}</th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">Balance</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 min-w-[120px]">Due Date</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 min-w-[180px]">Accrual Date</th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700 min-w-[200px]">Settlement Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td className="p-4 text-sm text-gray-500" colSpan={15}>
                        {debouncedTerm ? `No results found for "${debouncedTerm}"` : "No receivables found for this period"}
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((txn, idx) => (
                      <tr key={txn.id || idx} className={`border-b transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} hover:bg-orange-50`}>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.invoice_number ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.filial_name ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.issuer_name ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 font-mono text-xs whitespace-nowrap">{txn.chart_code ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.chart_name ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 max-w-xs truncate" title={txn.memo}>{txn.memo ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap">{txn.status ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.amount != null ? USDollar.format(txn.amount) : "-"}</td>
                        <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.discount != null ? USDollar.format(txn.discount) : "-"}</td>
                        <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.fee != null ? USDollar.format(txn.fee) : "-"}</td>
                        <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{isVacationFree ? (txn.discount != null ? USDollar.format(txn.discount) : "-") : (txn.total != null ? USDollar.format(txn.total) : "-")}</td>
                        <td className="p-3 text-sm text-gray-700 text-right whitespace-nowrap">{txn.balance != null ? USDollar.format(txn.balance) : "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap min-w-[120px]">{txn.due_date ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap min-w-[180px]">{txn.accrual_date ?? "-"}</td>
                        <td className="p-3 text-sm text-gray-700 whitespace-nowrap min-w-[200px]">{txn.settlement_date ?? "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
          )}
        </div>

        {filteredRows.length > 0 && (
          <div className="p-4 border-t flex items-center justify-between bg-gray-50 text-sm flex-shrink-0">
            <div className="text-sm text-gray-600">Page {page} of {totalPages}</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
